import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';

export interface IApplicationConfigEntityAttributes extends IModelAttributes {
	key: string;
	description: string;
	value: string;
	tag: string;
	type: string;
	manuallyChanged: boolean;
}
@entity('ApplicationConfigEntity', 'Application Config')
export default class ApplicationConfigEntity extends Model implements IApplicationConfigEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The key for the application configuration. Is unique and used to identify the settings.
	 */
	@observable
	@attribute<ApplicationConfigEntity, string>()
	public key: string;

	/**
	 * The description for the application configuration to make it easier to know what is what.
	 */
	@observable
	@attribute<ApplicationConfigEntity, string>()
	public description: string;

	/**
	 * The value for the application configuration. Stored as a string and converted to the intended type.
	 */
	@observable
	@attribute<ApplicationConfigEntity, string>()
	public value: string;

	/**
	 * A string of tags that can help sort through the application settings.
	 */
	@observable
	@attribute<ApplicationConfigEntity, string>()
	public tag: string;

	/**
	 * The input type used to render this config in the admin UI.
	 * Recognised values: "text", "textarea", "bool", "colour", "icon",
	 * "enum::OptionA,OptionB,…", "entity::EntityName".
	 */
	@observable
	@attribute<ApplicationConfigEntity, string>()
	public type: string;

	/**
	 * Whether or not the application config has already been manually changed (this will prevent the data-seeding from over-writing the user set values.
	 */
	@observable
	@attribute<ApplicationConfigEntity, boolean>()
	public manuallyChanged: boolean;

	constructor(attributes?: Partial<IApplicationConfigEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IApplicationConfigEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.key !== undefined) {
				this.key = attributes.key;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.value !== undefined) {
				this.value = attributes.value;
			}
			if (attributes.tag !== undefined) {
				this.tag = attributes.tag;
			}
			if (attributes.type !== undefined) {
				this.type = attributes.type;
			}
			if (attributes.manuallyChanged !== undefined) {
				this.manuallyChanged = attributes.manuallyChanged;
			}
		}
	}
}
